/**
 * agentProtocol.js — the browser side of the mhi-agent WebSocket protocol (CONTRACT §2.3, §10.2).
 *
 * Pure: no import.meta, no DOM, no React. Node runs the tests directly.
 *
 *   validateServerMessage(msg)  every inbound frame passes through here before the reducer sees it
 *   buildHello / …Message       outbound frames, shaped exactly as the server expects
 *   resolveAgentUrl             relative endpoint ('/<agent prefix>/ws') -> absolute ws(s):// URL
 *
 * Unknown event types are rejected here (the reducer would ignore them anyway); the client never
 * guesses at a type it was not built for.
 */

/** Envelope event types (closed set, CONTRACT §2.3 table). */
export const ENVELOPE_TYPES = new Set([
  'case.created', 'case.updated', 'case.focus',
  'plan.set', 'plan.step',
  'tool.call', 'tool.result',
  'speech', 'ui.action',
  'wb.ui', 'wb.exec.start', 'wb.term.chunk', 'wb.exec.end', 'wb.browser.frame', 'wb.browser.action',
  'file.added', 'file.updated', 'output.published',
  'draft.created', 'draft.op', 'draft.updated',
  'approval.pending', 'approval.updated',
  'page.updated', 'comment.added', 'handoff',
  'memory.added', 'memory.updated', 'memory.deleted',
  'notification', 'agent.mode', 'telemetry.sample',
]);

/** Ephemeral events: seq null, never stored, never replayed. */
export const EPHEMERAL_TYPES = new Set(['wb.term.chunk', 'wb.browser.frame', 'telemetry.sample']);

/** Control frames the server sends outside the Envelope shape. */
export const CONTROL_TYPES = new Set(['welcome', 'snapshot', 'replay.done', 'pong', 'error']);

export const EVENT_TYPES = new Set([...ENVELOPE_TYPES, ...CONTROL_TYPES]);

/** ui.action types the agent path accepts (the voice path has the same minus `asset`). */
export const AGENT_UI_ACTION_TYPES = new Set([
  'select', 'highlight', 'explode', 'open_workspace', 'trace', 'reset', 'back', 'deeper', 'asset',
]);

const CLIENT_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isSeq = (v) => v === null || (Number.isInteger(v) && v >= 0);
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');

export function isValidClientId(id) {
  return typeof id === 'string' && CLIENT_ID_RE.test(id);
}

/**
 * A client id for `hello` and voice `session.start`: a UUID without dashes, 32 chars.
 * `randomUUID` is injected so the function stays pure (agentConfig passes crypto.randomUUID).
 */
export function makeClientId(randomUUID) {
  let raw = '';
  try { raw = typeof randomUUID === 'function' ? String(randomUUID()) : ''; } catch { raw = ''; }
  let id = raw.replace(/-/g, '').slice(0, 32);
  while (id.length < 32) id += Math.floor(Math.random() * 16).toString(16);
  return id;
}

/**
 * Resolve a configured endpoint against the page location.
 *   ws:   '/agent/ws' on https://host/app -> 'wss://host/agent/ws'
 *   http: '/agent'                        -> 'https://host/agent'
 * Absolute values pass through (http(s) is switched to ws(s) for the socket). '' stays ''.
 */
export function resolveAgentUrl(value, loc, { ws = false } = {}) {
  const v = String(value ?? '').trim();
  if (!v) return '';
  if (/^wss?:\/\//i.test(v)) return ws ? v : v.replace(/^ws/i, 'http');
  if (/^https?:\/\//i.test(v)) return ws ? v.replace(/^http/i, 'ws') : v;
  const protocol = loc?.protocol === 'https:' ? 'https:' : 'http:';
  const host = loc?.host || 'localhost';
  const path = v.startsWith('/') ? v : `/${v}`;
  const base = `${protocol}//${host}${path}`;
  return ws ? base.replace(/^http/, 'ws') : base.replace(/\/+$/, '');
}

/** UiAction shape check (CONTRACT §4.3.2). */
export function validUiAction(action) {
  if (!isObject(action) || typeof action.type !== 'string') return false;
  if (!AGENT_UI_ACTION_TYPES.has(action.type)) return false;
  if ('part_ids' in action && !isStringArray(action.part_ids)) return false;
  if ('t' in action && action.t !== undefined
    && (typeof action.t !== 'number' || !Number.isFinite(action.t) || action.t < 0 || action.t > 1)) return false;
  if ((action.type === 'select' || action.type === 'highlight')
    && !(Array.isArray(action.part_ids) && action.part_ids.length > 0)) return false;
  if (action.type === 'explode' && typeof action.t !== 'number') return false;
  if (action.type === 'trace' && (typeof action.fault_id !== 'string' || !action.fault_id)) return false;
  if (action.type === 'asset' && (typeof action.asset !== 'string' || !action.asset)) return false;
  for (const key of ['roles', 'ranks', 'colors']) {
    if (key in action && action[key] !== undefined && action[key] !== null && !isObject(action[key])) return false;
  }
  return true;
}

const fail = (reason) => ({ ok: false, reason });

/**
 * -> {ok:true, msg} | {ok:false, reason}. Checks the type string and allow-list, the data object
 * and seq (int|null) of Envelopes, and the shapes the reducer relies on for control frames.
 */
export function validateServerMessage(msg) {
  if (!isObject(msg)) return fail('not_object');
  if (typeof msg.type !== 'string' || !msg.type) return fail('bad_type');
  if (!EVENT_TYPES.has(msg.type)) return fail('unknown_type');

  switch (msg.type) {
    case 'welcome':
      if (msg.principal !== undefined && msg.principal !== null && !isObject(msg.principal)) return fail('bad_principal');
      if (msg.server_seq !== undefined && !isSeq(msg.server_seq)) return fail('bad_seq');
      return { ok: true, msg };
    case 'snapshot':
      if (!isObject(msg.data)) return fail('bad_data');
      if (!Number.isInteger(msg.seq) || msg.seq < 0) return fail('bad_seq');
      return { ok: true, msg };
    case 'replay.done':
      if (!Number.isInteger(msg.seq) || msg.seq < 0) return fail('bad_seq');
      return { ok: true, msg };
    case 'pong':
      return { ok: true, msg };
    case 'error':
      if (typeof msg.code !== 'string') return fail('bad_error');
      return { ok: true, msg };
    default:
      break;
  }

  // Envelope.
  if (!isObject(msg.data)) return fail('bad_data');
  if (!('seq' in msg) || !isSeq(msg.seq)) return fail('bad_seq');
  if (EPHEMERAL_TYPES.has(msg.type) && msg.seq !== null) return fail('bad_seq');
  if (msg.case_id !== undefined && msg.case_id !== null && typeof msg.case_id !== 'string') return fail('bad_case_id');
  if (msg.type === 'ui.action' && !validUiAction(msg.data.action)) return fail('bad_ui_action');
  return { ok: true, msg };
}

/* ------------------------------------------------------------- outbound */

export function buildHello({ clientId, lastSeq = 0, demoRole = null } = {}) {
  const seq = Number.isInteger(lastSeq) && lastSeq > 0 ? lastSeq : 0;
  const msg = { type: 'hello', client_id: clientId, last_seq: seq, subscribe: 'all' };
  if (demoRole) msg.demo_role = demoRole;
  return msg;
}

export const pingMessage = () => ({ type: 'ping' });

export const focusMessage = (focus) => ({ type: 'focus', focus });

export const caseOpenMessage = (caseId) => ({ type: 'case.open', case_id: caseId });

export const intentMessage = (caseId, text) => ({ type: 'intent', case_id: caseId ?? null, text: String(text ?? '') });

const CONTROL_EXTRA_KEYS = ['step_id', 'step_index', 'query', 'reason'];

export function controlMessage(caseId, op, extra = {}) {
  const msg = { type: 'control', case_id: caseId, op };
  for (const key of CONTROL_EXTRA_KEYS) {
    if (extra && extra[key] !== undefined && extra[key] !== null) msg[key] = extra[key];
  }
  return msg;
}

export function decideMessage(approvalId, decision, { draftVersion = null, reason } = {}) {
  const msg = {
    type: 'approval.decide',
    approval_id: approvalId,
    decision,
    draft_version: Number.isInteger(draftVersion) ? draftVersion : null,
  };
  if (reason) msg.reason = String(reason);
  return msg;
}

export const draftOpMessage = (caseId, draftId, baseVersion, op) => ({
  type: 'draft.op', case_id: caseId, draft_id: draftId, base_version: baseVersion, op,
});

export function commentMessage(caseId, body, parentId = null) {
  const msg = { type: 'comment', case_id: caseId, body: String(body ?? '') };
  if (parentId) msg.parent_id = parentId;
  return msg;
}

export const roleMessage = (role) => ({ type: 'role', demo_role: role });

export function replayMessage(scenario, speed = 1, model = null) {
  const msg = { type: 'replay.start', scenario, speed: Number.isFinite(Number(speed)) ? Number(speed) : 1 };
  if (model) msg.model = model;
  return msg;
}
